// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import React, { SyntheticEvent } from "react";
import { getDarkTheme, getPrimaryRedTheme } from "../../../../common/themes";
import {
    Customizer,
    Dialog,
    DialogContent,
    DialogFooter,
    DialogType,
    IconButton,
    ICustomizations,
    ITheme,
    PrimaryButton,
} from "@fluentui/react";
import {
    IFormRegion,
    ILabel,
    IProject,
} from "../../../../models/applicationState";
import "./editLabelDialog.scss";

/**
 * Properties for Alert Component
 * @member closeButtonText - Text displayed on 'Close' button. Default 'OK'
 * @member onClose - Function to execute on alert close
 * @member confirmButtonTheme - Theme of 'Confirm' button
 */
export interface IEditLabelDialogProps {
    closeButtonText?: string;
    confirmButtonTheme?: ITheme;
    show?: boolean;
    container?: IProject;
    label?: ILabel;
    onClose?: () => void;
    onSaveEditLabelClick?: (sender: SyntheticEvent, value: ILabel) => void;
}

/**
 * State for Alert Component
 * @member params - Arguments passed in the open command
 */
export interface IEditLabelDialogState {
    label?: ILabel;
    regionToEdit?: IFormRegion;
    regionToEditIndex?: number;
    hasError: boolean;
}

/**
 * @name - Alert
 * @description - Generic Alert dialog
 */
export default class EditLabelDialog extends React.Component<
    IEditLabelDialogProps,
    IEditLabelDialogState
> {
    prevFormValue: string = '';
    // hachet
    constructor(props, context) {
        super(props, context);

        this.onCloseClick = this.onCloseClick.bind(this);
        this.onSaveEditLabelClick = this.onSaveEditLabelClick.bind(this);
        this.updateRegion = this.updateRegion.bind(this);
        this.undoEdit = this.undoEdit.bind(this);
        this.onInputChanged = this.onInputChanged.bind(this);

        this.state = {
            label: this.props.label,
            regionToEditIndex: -1,
            hasError: false
        };
    }

    // componentWillReceiveProps() {
    //     if (this.props.label) {
    //         const json = JSON.stringify(this.props.label);
    //         const newLabel = JSON.parse(json);

    //         this.setState({
    //             label: newLabel,
    //         });
    //     } else {
    //         this.setState({
    //             label: null,
    //         });
    //     }
    // }

    componentDidUpdate(prevProps: Readonly<IEditLabelDialogProps>, prevState: Readonly<IEditLabelDialogState>) {
        if (this.props.label && JSON.stringify(this.props.label) !== JSON.stringify(prevProps.label)) {
            const json = JSON.stringify(this.props.label);
            const newLabel = JSON.parse(json);

            this.setState({
                label: newLabel,
            });
        }
    }

    private inputRef = React.createRef<HTMLInputElement>();

    public render() {
        const dark: ICustomizations = {
            settings: {
                theme: getDarkTheme(),
            },
            scopedSettings: {},
        };        

        return (
            
                <Dialog
                    onDismiss={this.onCloseClick}
                    hidden={!this.props.show}
                    dialogContentProps={{
                        type: DialogType.normal,
                        title: `Please select a text to edit`,
                        className: "edit-label-header"
                    }}
                    minWidth="60%"
                    modalProps={{
                        isBlocking: true
                    }}                    
                >
                    <DialogContent className="edit-label-dialog">
                    {this.state.label &&
                        this.state.label.value.length > 0 &&
                        <div className="edit-label-container">
                            {                                
                                this.state.label.value.map((formValue, index) => {         
                                    if (this.state.regionToEdit && this.state.regionToEditIndex === index) {
                                        return (
                                            <div
                                                key={index}
                                                className="edit-label-input-container"
                                            >
                                                <input
                                                    type="text"
                                                    placeholder="Edit the text here"
                                                    ref={this.inputRef}
                                                    readOnly={false}
                                                    onKeyDown={this.onInputKeyDown}
                                                    onChange={this.onInputChanged}
                                                    className={`edit-label-input ${
                                                        this.state.hasError
                                                            ? "input-error"
                                                            : ""
                                                    }`}
                                                />
                                                {this.state.hasError && (
                                                    <span className="error-message">
                                                        Please enter a valid value.
                                                    </span>
                                                )}
                                            </div>
                                            // <input
                                            //     //className="tag-input-box"
                                            //     type="text"
                                            //     placeholder="Please select a text to edit"
                                            //     ref={this.inputRef}
                                            //     readOnly={false}
                                            //     onKeyDown={this.onInputKeyDown}
                                            //     onChange={this.onInputChanged}
                                            //     style={this.state.hasError ? {borderColor: 'red'} : {}}                                                                                                
                                            // />
                                        )
                                    }
                                    else {
                                        return (
                                            <label
                                                onClick={() =>
                                                    this.onLabelClicked(formValue, index)
                                                }
                                                className="mr-1 edit-label"
                                                key={index}
                                            >
                                                {formValue.text}
                                            </label>
                                        )
                                    }                                    
                                })
                            }
                        </div>
                    }

                    {/* {this.state.regionToEdit && (
                        <div className="row">
                            <div className="label-input-container">
                                <input
                                    //className="tag-input-box"
                                    type="text"
                                    placeholder="Please select a text to edit"
                                    ref={this.inputRef}
                                    readOnly={false}
                                    onKeyDown={this.onInputKeyDown}
                                    onChange={this.onInputChanged}
                                />
                            </div>
                        </div>
                    )} */}
                    </DialogContent>
                    <DialogFooter className="ma-0">
                        <div className="text-right">
                            <PrimaryButton
                                style={{ marginRight: "10px" }}
                                theme={getPrimaryRedTheme()}
                                onClick={this.onSaveEditLabelClick}
                                text="Save"
                                disabled={this.state.hasError}
                            />
                            <PrimaryButton
                                theme={getDarkTheme()}
                                onClick={this.onCloseClick}
                                text="Close"
                            />
                        </div>
                        <div
                            className="text-left"
                            style={{ fontSize: "12px", color: "#eeabab" }}
                        >
                            Warning: You cannot "undo" once you click the save
                            button.
                        </div>
                    </DialogFooter>
                </Dialog>
            
        );
    }

    private onInputChanged(e) {
        const { value } = e.target;
        if (this.state.label && this.state.regionToEdit) {
            let region = this.state.label.value[this.state.regionToEditIndex];
            if (region) {
                region.text = value;     
                this.setState({
                    label: { ...this.state.label }, 
                    hasError: !value
                });
            }
        }
        //e.target.value = e.target.value.replace(/\s/g, "");
    }

    private onInputKeyDown(e) {
        //if (e.which === 32) return false;
    }

    private updateRegion() {
        if (this.state.label && this.state.regionToEdit) {
            const region = this.state.label.value[this.state.regionToEditIndex];
            if (region) {
                region.text = this.inputRef.current.value;
                this.setState({
                    label: { ...this.state.label },
                    regionToEdit: null,
                    regionToEditIndex: -1,
                });
            }
        }
    }

    private undoEdit() {
        const propsRegion =
            this.props.label.value[this.state.regionToEditIndex];

        let stateRegion = this.state.label.value[this.state.regionToEditIndex];
        stateRegion = propsRegion;
        this.inputRef.current.value = propsRegion.text;

        this.setState({
            label: { ...this.state.label },
        });
    }

    private onLabelClicked(formValue: IFormRegion, index: number) {
        this.setState({
            regionToEdit: { ...formValue },
            regionToEditIndex: index,
            hasError: false
        });

        setTimeout(() => {
            if (this.inputRef.current) {
                this.inputRef.current.value = formValue.text;
                this.inputRef.current.focus();
                this.inputRef.current.setSelectionRange(
                    0,
                    this.inputRef.current.value.length
                );
            }
        });
    }

    public clearState() {
        // this.setState({
        //     searching: false,
        //     searchResult: [],
        // });
    }

    private onSaveEditLabelClick(e: any) {
        //this.updateRegion();
        this.props.onSaveEditLabelClick(e, this.state.label);
        this.onCloseClick();
    }

    private onCloseClick() {
        if (this.inputRef.current) {
            this.inputRef.current.value = "";
        }

        this.setState({
            label: { ...this.state.label },
            regionToEdit: null,
            regionToEditIndex: -1,
            hasError: false
        });

        if (this.props.onClose) {
            setTimeout(() => {
                this.props.onClose(); // Call the parent function to update the `show` prop
            }, 300); // Add a delay (e.g., 300ms) to allow the dialog to close smoothly
        }
    }
}
